import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import classNames from 'src/modules/shared/classNames';
import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import EntitlementService from 'src/modules/entitlement/entitlementService';
import { useHistory, Redirect } from "react-router-dom"
import { getHistory } from 'src/modules/store';
import authSelectors from 'src/modules/auth/authSelectors';
import { useDispatch, useSelector } from 'react-redux';
import service from 'src/modules/auth/authService';
import PermissionChecker from 'src/modules/auth/permissionChecker';
import * as toastr from 'toastr';
import 'toastr/build/toastr.css';
/*
couponCode={couponCode}
                                  couponCodeAppied={couponCodeAppied}
*/

// Define window.AF for tracking
declare global {
  interface Window {
    AF?: (type: string, event: string, data: any) => void;
  }
}

// Define interface to avoid type errors
export interface CheckoutFormV2Props {
  priceID: string;
  state: string;
  setState: (state: string) => void;
  subscriptionId: any;
  couponCode: string;
  couponCodeAppied: string | null;
  amount: number;
  selectedPlan: any;
  onPaymentComplete: (success: boolean) => void;
  customStyles?: any;
  clientSecret?: any;
  cardholderName: string;
  couponId: string | null;
}

export interface CheckoutFormV2Ref {
  submitPayment: () => boolean;
}

const CheckoutFormV2 = forwardRef<CheckoutFormV2Ref, CheckoutFormV2Props>((props, ref) => {
  const {
    priceID,
    state,
    setState,
    subscriptionId,
    couponCode,
    couponCodeAppied,
    amount,
    selectedPlan,
    onPaymentComplete,
    customStyles = {},
    cardholderName,
    couponId
  } = props;
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();

  // Initialize validation errors as null to prevent immediate display
  const [validationErrors, setValidationErrors] = useState({
    cardNumber: null as string | null,
    cardExpiryDate: null as string | null,
    cardCvc: null as string | null
  });

  // Track if a validation has been attempted - starts as false to prevent immediate validation
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Track touched fields to only show validation for fields that have been interacted with
  const [touchedFields, setTouchedFields] = useState({
    cardNumber: false,
    cardExpiryDate: false,
    cardCvc: false
  });

  const [countries, setCountries] = useState([]);

  // Expose submitPayment method to parent components
  useImperativeHandle(ref, () => ({
    submitPayment: () => {
      console.log("submitPayment called");
      setSubmitAttempted(true);
      const isValid = handleValidation();
      if (isValid) {
        processPayment();
      }
      return isValid; // Return validation result to parent
    }
  }));

  // Handle changes to Stripe Elements
  const handleElementChange = (elementType) => (event) => {
    // Mark the field as touched
    setTouchedFields(prev => ({
      ...prev,
      [elementType]: true
    }));

    // Only clear validation errors if field is not empty
    if (!event.empty) {
      setValidationErrors(prev => ({
        ...prev,
        [elementType]: null
      }));
    }
  };

  const currentUser = useSelector(
    authSelectors.selectCurrentUser,
  );

  const processPayment = async () => {
    if (!stripe || !elements) {
      return;
    }

    setState("loading");
    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) {
      console.error("Card element not found");
      setState("idle");
      if (onPaymentComplete) {
        onPaymentComplete(false);
      }
      return;
    }

    try {
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          name: cardholderName
        }
      });

      if (error) {
        console.error(error);
        setState("idle");
        if (onPaymentComplete) {
          onPaymentComplete(false);
        }
      } else {
        // Create token with the card element
        const tokenResult = await stripe.createToken(cardNumberElement, {
          name: cardholderName
        });

        if (tokenResult.error) {
          console.error('Token creation error:', tokenResult.error);
          setState("idle");
          if (onPaymentComplete) {
            onPaymentComplete(false);
          }
          return;
        }

        const data = {
          "cardToken": tokenResult.token?.id,
          "priceId": priceID,
          "coupon": couponId ? couponId : "",
        };

        try {
          const res = await EntitlementService.payment(data);
          
          if (!res || (res as any).error) {
            toastr.error((res as any)?.error?.message || "Payment failed. Please try again.");
            setState("idle");
            if (onPaymentComplete) {
              onPaymentComplete(false);
            }
            return;
          }

          const response = await service.fetchMe();

          setState("idle");
          toastr.options.toastClass = 'toastr';
          toastr.options.positionClass = 'toast-bottom-left';

          // Handle AF tracking in a safe way
          try {
            if (typeof window !== 'undefined' && window.AF) {
              window.AF('pba', 'event', {
                "eventType": "EVENT",
                "eventValue": {
                  "af_currency": `USD`,
                  "af_revenue": amount,
                  "new_subscription": true,
                  "subscription_method": "credit card",
                  "expiration_date": (new Date(new Date().setDate(new Date().getDate() + 7)).toLocaleDateString()),
                  "af_coupon_code": couponCode,
                  "af_order_ID": "",
                  "af_param_2": selectedPlan.renewalPeriod
                },
                "eventName": "af_subscribe"
              });

              window.AF('pba', 'event', {
                "eventType": "EVENT",
                "eventValue": {
                  "start_trial_device": `web`,
                  "expiration_date": (new Date(new Date().setDate(new Date().getDate() + 7)).toLocaleDateString())
                },
                "eventName": "af_start_trial"
              });
            }
          } catch (e) {
            console.error('Error with AF tracking:', e);
          }

          toastr.success("Your payment has been received...");
          if (onPaymentComplete) {
            onPaymentComplete(true);
          }
        } catch (error) {
          console.error('Payment error:', error);
          toastr.options.toastClass = 'toastr';
          toastr.options.positionClass = 'toast-bottom-left';
          toastr.error("Something went wrong please try again later...");
          setState("idle");
          if (onPaymentComplete) {
            onPaymentComplete(false);
          }
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toastr.options.toastClass = 'toastr';
      toastr.options.positionClass = 'toast-bottom-left';
      toastr.error("Something went wrong please try again later...");
      setState("idle");
      if (onPaymentComplete) {
        onPaymentComplete(false);
      }
    }
  }

  const handleValidation = () => {
    const cardNumberElement = elements?.getElement(CardNumberElement);
    const cardExpiryElement = elements?.getElement(CardExpiryElement);
    const cardCvcElement = elements?.getElement(CardCvcElement);
    let isValid = true;
    const newErrors = { ...validationErrors };

    // Check if cardNumberElement is available and validate it
    if (!cardNumberElement) {
      isValid = false;
      newErrors.cardNumber = 'Card Number is required';
    } else {
      // We can't directly access the internal state, so we have to check using the DOM
      const emptyCardNumber = !document.querySelector('#cardNumber .StripeElement--complete');
      const invalidCardNumber = document.querySelector('#cardNumber .StripeElement--invalid');

      if (emptyCardNumber) {
        isValid = false;
        newErrors.cardNumber = 'Card Number is required';
      } else if (invalidCardNumber) {
        isValid = false;
        newErrors.cardNumber = 'Invalid Card Number';
      } else {
        newErrors.cardNumber = null;
      }
    }

    // Check if cardExpiryElement is available and validate it
    if (!cardExpiryElement) {
      isValid = false;
      newErrors.cardExpiryDate = 'Expiry date is required';
    } else {
      const emptyCardExpiry = !document.querySelector('#cardExp .StripeElement--complete');
      const invalidCardExpiry = document.querySelector('#cardExp .StripeElement--invalid');

      if (emptyCardExpiry) {
        isValid = false;
        newErrors.cardExpiryDate = 'Expiry date is required';
      } else if (invalidCardExpiry) {
        isValid = false;
        newErrors.cardExpiryDate = 'Invalid Expiry date';
      } else {
        newErrors.cardExpiryDate = null;
      }
    }

    // Check if cardCvcElement is available and validate it
    if (!cardCvcElement) {
      isValid = false;
      newErrors.cardCvc = 'CVC is required';
    } else {
      const emptyCardCvc = !document.querySelector('#cardCvc .StripeElement--complete');
      const invalidCardCvc = document.querySelector('#cardCvc .StripeElement--invalid');

      if (emptyCardCvc) {
        isValid = false;
        newErrors.cardCvc = 'CVC is required';
      } else if (invalidCardCvc) {
        isValid = false;
        newErrors.cardCvc = 'Invalid CVC';
      } else {
        newErrors.cardCvc = null;
      }
    }

    // We now validate cardholderName in PaymentSection

    setValidationErrors(newErrors);
    return isValid && cardholderName.trim() !== '';
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch('https://restcountries.com/v3.1/all');
        const data = await response.json();
        setCountries(data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchCountries();
  }, []);

  // Apply custom styles to the Stripe Elements
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        '::placeholder': {
          color: '#ffffff',
          opacity: '0.6'
        },
        ...customStyles?.base,
        lineHeight: '25px',
        padding: '0'
      },

      invalid: {
        color: '#E88697',
        ...customStyles?.invalid
      },
    },
    placeholder: 'Card Number',
  };

  // Đặt options riêng cho từng trường
  const cardNumberOptions = {
    style: cardElementOptions.style,
    placeholder: 'Card Number',
  };
  const cardExpiryOptions = {
    style: cardElementOptions.style,
    placeholder: 'MM/YY',
  };
  const cardCvcOptions = {
    style: cardElementOptions.style,
    placeholder: 'CVC',
  };

  // Only show error if field has been touched or form submission attempted
  const shouldShowError = (fieldName) => {
    return (submitAttempted || touchedFields[fieldName]) && validationErrors[fieldName];
  };

  return (
    <div className="w-full">
      <div className="">
        <div className='mb-[10px]'>
          <div id="cardNumber" className="relative">
            {/* @ts-ignore: Stripe component type errors */}
            <CardNumberElement
              onChange={handleElementChange('cardNumber')}
              className={`bg-white/30 rounded-md !py-[16px]  text-white !focus:ring-1 !focus:ring-blue-400 border border-transparent !mb-0 ${shouldShowError('cardNumber') ? ' !border-[#E88697]' : ''}`}
              options={{
                style: {
                  base: {
                    color: '#fff',
                    '::placeholder': {
                      color: 'rgba(255, 255, 255, 0.6)',
                    },
                  },
                },
                placeholder: 'Card Number'
              }}
            />
          </div>
        </div>

        <div className='grid grid-cols-2 gap-[10px]'>
          <div>
            {/* {shouldShowError('cardExpiryDate') &&
              <div className="text-red-600 mb-2 text-xs md:text-sm">{validationErrors.cardExpiryDate}</div>
            } */}
            <div id="cardExp" className="relative">
              {/* @ts-ignore: Stripe component type errors */}
              <CardExpiryElement
                onChange={handleElementChange('cardExpiryDate')}
                className={`bg-white/30 rounded-md !py-[16px] !px-[5px] text-white !focus:ring-1 !focus:ring-blue-400 border border-transparent !mb-0 ${shouldShowError('cardNumber') ? ' !border-[#E88697]' : ''}`}
                options={{
                  style: {
                    base: {
                      color: '#fff',
                      '::placeholder': {
                        color: 'rgba(255, 255, 255, 0.6)',
                      },
                    },
                  },
                  placeholder: 'MM/YY'
                }}
              />
            </div>
          </div>
          <div>
            {/* {shouldShowError('cardCvc') &&
              <div className="text-red-600 mb-2 text-xs md:text-sm">{validationErrors.cardCvc}</div>
            } */}
            <div id="cardCvc" className="relative">
              {/* @ts-ignore: Stripe component type errors */}
              <CardCvcElement
                onChange={handleElementChange('cardCvc')}
                className={`bg-white/30 rounded-md !py-[16px] !px-[5px] text-white !focus:ring-1 !focus:ring-blue-400 border border-transparent !mb-0 ${shouldShowError('cardNumber') ? ' !border-[#E88697]' : ''}`}
                options={{
                  style: {
                    base: {
                      color: '#fff',
                      '::placeholder': {
                        color: 'rgba(255, 255, 255, 0.6)',
                      },
                    },
                  },
                  placeholder: 'CVC'
                }}
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg width="30" height="20" viewBox="0 0 30 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g opacity="0.6">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M28.45 20H2.431C1.57792 20 0.882812 19.3049 0.882812 18.4518V1.54818C0.882812 0.695103 1.57792 0 2.431 0H28.45C29.303 0 29.9981 0.695103 29.9981 1.54818V18.4518C29.9981 19.3049 29.303 20 28.45 20ZM2.431 0.916272C2.07555 0.916272 1.79119 1.20063 1.79119 1.55608V18.4597C1.79119 18.8152 2.07555 19.0995 2.431 19.0995H28.45C28.8054 19.0995 29.0898 18.8152 29.0898 18.4597V1.55608C29.0898 1.20063 28.8054 0.916272 28.45 0.916272H2.431Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M18.996 13.4775C18.4114 13.4775 17.9375 13.0036 17.9375 12.4191V11.5897C17.9375 11.0052 18.4114 10.5312 18.996 10.5312C19.5805 10.5312 20.0544 11.0052 20.0544 11.5897V12.4191C20.0544 13.0036 19.5805 13.4775 18.996 13.4775ZM18.996 11.4396C18.917 11.4396 18.8459 11.5107 18.8459 11.5897V12.4191C18.8459 12.4981 18.917 12.5692 18.996 12.5692C19.0749 12.5692 19.146 12.4981 19.146 12.4191V11.5897C19.146 11.5107 19.0749 11.4396 18.996 11.4396Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M21.8726 13.4732C21.6198 13.4732 21.4144 13.2678 21.4144 13.015V11.8223C21.3196 11.8065 21.2249 11.767 21.1538 11.6959C20.98 11.5142 20.98 11.2299 21.1538 11.0561L21.5487 10.6611C21.6751 10.5348 21.8726 10.4953 22.0463 10.5664C22.2201 10.6374 22.3307 10.8033 22.3307 10.985V13.0229C22.3228 13.2678 22.1253 13.4732 21.8726 13.4732Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M24.1129 13.4732C23.8601 13.4732 23.6547 13.2678 23.6547 13.015V11.8223C23.5599 11.8065 23.4652 11.767 23.3941 11.6959C23.2124 11.5221 23.2124 11.2299 23.3941 11.0561L23.789 10.6611C23.9154 10.5348 24.1129 10.4953 24.2866 10.5664C24.4604 10.6374 24.571 10.8033 24.571 10.985V13.0229C24.5631 13.2678 24.3577 13.4732 24.1129 13.4732Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M26.6328 15.3947H4.23939C3.98662 15.3947 3.78125 15.1893 3.78125 14.9366V9.05189C3.78125 8.79912 3.98662 8.59375 4.23939 8.59375H26.6328C26.8855 8.59375 27.0909 8.79912 27.0909 9.05189V14.9366C27.0909 15.1893 26.8855 15.3947 26.6328 15.3947ZM4.69752 14.4863H26.1825V9.51002H4.69752V14.4863Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7.53361 12.6561C7.47832 12.6561 7.41513 12.6482 7.35984 12.6245L6.28559 12.1901C6.05652 12.0953 5.93804 11.8268 6.03282 11.5977C6.12761 11.3686 6.39617 11.2501 6.62524 11.3449L7.07548 11.5266V10.9816C7.07548 10.7288 7.28085 10.5234 7.53361 10.5234C7.78638 10.5234 7.99175 10.7288 7.99175 10.9816V12.198C7.99175 12.3481 7.91276 12.4903 7.79428 12.5771C7.71529 12.6245 7.6284 12.6561 7.53361 12.6561Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.19595 13.4831C8.06166 13.4831 7.92738 13.4278 7.84049 13.3094L7.17699 12.48C7.0901 12.3694 7.0585 12.2193 7.0901 12.0771C7.12959 11.9349 7.22438 11.8244 7.35866 11.7691L8.43291 11.3346C8.66198 11.2398 8.93054 11.3504 9.02533 11.5874C9.12012 11.8244 9.00953 12.085 8.77256 12.1798L8.26703 12.3852L8.5514 12.7406C8.70937 12.9381 8.67778 13.2225 8.4803 13.3804C8.40132 13.4436 8.29863 13.4831 8.19595 13.4831Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M6.87003 13.4857C6.76735 13.4857 6.67256 13.4541 6.58567 13.383C6.3882 13.225 6.35661 12.9407 6.51458 12.7432L7.17809 11.9138C7.33607 11.7163 7.62043 11.6847 7.8179 11.8427C8.01537 12.0007 8.04697 12.285 7.88899 12.4825L7.22548 13.3119C7.1386 13.4225 7.00432 13.4857 6.87003 13.4857Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M11.4164 12.6561C11.3611 12.6561 11.2979 12.6482 11.2427 12.6245L10.1684 12.1901C9.93933 12.0953 9.82085 11.8268 9.91564 11.5977C10.0104 11.3686 10.279 11.2501 10.5081 11.3449L10.9583 11.5266V10.9816C10.9583 10.7288 11.1637 10.5234 11.4164 10.5234C11.6692 10.5234 11.8746 10.7288 11.8746 10.9816V12.198C11.8746 12.3481 11.7956 12.4903 11.6771 12.5771C11.5981 12.6245 11.5033 12.6561 11.4164 12.6561Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M12.0788 13.4831C11.9445 13.4831 11.8102 13.4278 11.7233 13.3094L11.0598 12.48C10.9729 12.3694 10.9413 12.2193 10.9729 12.0771C11.0124 11.9349 11.1072 11.8244 11.2415 11.7691L12.3157 11.3346C12.5448 11.2398 12.8134 11.3504 12.9081 11.5874C13.0029 11.8165 12.8923 12.085 12.6554 12.1798L12.1498 12.3852L12.4342 12.7406C12.5922 12.9381 12.5606 13.2225 12.3631 13.3804C12.2841 13.4436 12.1814 13.4831 12.0788 13.4831Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M10.7528 13.4857C10.6502 13.4857 10.5554 13.4541 10.4685 13.383C10.271 13.225 10.2394 12.9407 10.3974 12.7432L11.0609 11.9138C11.2189 11.7163 11.5032 11.6847 11.7007 11.8427C11.8982 12.0007 11.9298 12.285 11.7718 12.4825L11.1083 13.3119C11.0135 13.4225 10.8871 13.4857 10.7528 13.4857Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M15.3071 12.6561C15.2518 12.6561 15.1886 12.6482 15.1333 12.6245L14.059 12.1901C13.83 12.0953 13.7115 11.8268 13.8063 11.5977C13.901 11.3686 14.1696 11.2501 14.3987 11.3449L14.8489 11.5266V10.9816C14.8489 10.7288 15.0543 10.5234 15.3071 10.5234C15.5598 10.5234 15.7652 10.7288 15.7652 10.9816V12.198C15.7652 12.3481 15.6862 12.4903 15.5677 12.5771C15.4808 12.6245 15.3939 12.6561 15.3071 12.6561Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M15.9694 13.4831C15.8351 13.4831 15.7008 13.4278 15.6139 13.3094L14.9504 12.48C14.8635 12.3694 14.8319 12.2193 14.8635 12.0771C14.903 11.9349 14.9978 11.8244 15.1321 11.7691L16.2063 11.3346C16.4354 11.2398 16.704 11.3504 16.7988 11.5874C16.8936 11.8165 16.783 12.085 16.546 12.1798L16.0405 12.3852L16.3248 12.7406C16.4828 12.9381 16.4512 13.2225 16.2537 13.3804C16.1669 13.4436 16.0721 13.4831 15.9694 13.4831Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M14.6435 13.4857C14.5408 13.4857 14.446 13.4541 14.3591 13.383C14.1616 13.225 14.13 12.9407 14.288 12.7432L14.9515 11.9138C15.1095 11.7163 15.3939 11.6847 15.5913 11.8427C15.7888 12.0007 15.8204 12.285 15.6624 12.4825L14.9989 13.3119C14.9041 13.4225 14.7778 13.4857 14.6435 13.4857Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M29.5401 6.14468H1.33314C1.08037 6.14468 0.875 5.93931 0.875 5.68654V3.55384C0.875 3.30107 1.08037 3.0957 1.33314 3.0957H29.5401C29.7929 3.0957 29.9982 3.30107 29.9982 3.55384V5.68654C29.9982 5.93931 29.7929 6.14468 29.5401 6.14468ZM1.79127 5.2363H29.0899V4.01987H1.79127C1.79127 4.01197 1.79127 5.2363 1.79127 5.2363Z" fill="white" />
                  </g>
                </svg>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
});

export default CheckoutFormV2;